import { Component, ChangeDetectionStrategy, signal, HostBinding, Input, effect, Inject, PLATFORM_ID } from "@angular/core";
import { ButtonModule } from "core/components/button/button.module";
import { iconAngleDown } from "main/icons/icon-angle-down";
import { iconCursor } from "main/icons/icon-cursor";
import { iconLogIn } from "main/icons/icon-log-in";
import { FeaturesListComponent, FeaturesTuple } from "../features-list/features-list.component";
import { iconDelivery } from "main/icons/icon-delivery";
import { iconFoldersArchive } from "main/icons/icon-folders-archive";
import { iconHeart } from "main/icons/icon-heart";
import { iconMoney } from "main/icons/icon-money";
import { iconUsersGroup } from "main/icons/icon-users-group";
import { iconWarehouse } from "main/icons/icon-warehouse";
import { iconEcommerceSquare } from "main/icons/icon-ecommerce-square";
import { iconMobileSquare } from "main/icons/icon-mobile-square";
import { iconServicesSquare } from "main/icons/icon-services-square";
import { iconInventorySquare } from "main/icons/icon-inventory-square";
import { TipModule } from "core/components/tip/tip.module";
import { MediaQuery } from "core/services/media-query.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { iconMenu } from "main/icons/icon-menu";
import { IconsModule } from "core/components/icons/icons.module";
import { MobileMenuComponent } from "../mobile-menu/mobile-menu.component";
import { iconCloseBold } from "main/icons/icon-close-bold";
import { DOCUMENT, isPlatformBrowser, NgClass } from "@angular/common";
import { RouterBehavior } from "core/utils/router";
import { TimeOfDayType } from "src/app/ui-components/gradient-page/page-gradient.config";
import { GradientPageDirective } from "src/app/ui-components/gradient-page/gradient-page.directive";
import { TimeOfDayService } from "src/app/ui-components/gradient-page/time-of-day.service";
import { iconSupport } from "main/icons/icon-support";
import { LocaleService } from "src/app/locale/locale.service";
import { NavigationEnd, NavigationSkipped, Router, RouterLinkActive } from '@angular/router';
import { RoutesEnum } from "src/app/locale/routes.enum";
import { ModalService } from "core/modal/services/modal.service";
import { iconStopwatch } from "main/icons/icon-stopwatch";
import { iconLogo } from "main/icons/icon-logo";
import { SafeHtmlPipe } from "core/pipes/safe-html.pipe";
import { TranslateModule } from "@ngx-translate/core";
import { WindowService } from "core/services/window.service";
import { AppService } from "src/app/app.service";
import { TouchRouterLinkDirective } from 'core/directives/touch-router-link.directive';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  host: { ngSkipHydration: 'true' },
  standalone: true,
  imports: [ButtonModule, TipModule, IconsModule, TranslateModule, SafeHtmlPipe, RouterLinkActive, TouchRouterLinkDirective, GradientPageDirective, NgClass, MobileMenuComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
    readonly angleDownIcon = iconAngleDown;
    readonly supportIcon = iconSupport;
    readonly loginIcon = iconLogIn;
    readonly logoIcon = iconLogo;
    readonly menuIcon = iconMenu;
    readonly cursorIcon = iconCursor;
    readonly closeIcon = iconCloseBold;
    readonly tipComponent = FeaturesListComponent;

    @Input()
    hasPromoLine?: boolean;

    $dark = signal(false);
    $transparent = signal(false);

    routesEnum: typeof RoutesEnum = RoutesEnum;
    $showOnlyLogin = signal<boolean>(false);
    $showOnlyRegister = signal<boolean>(false);
    $showOnlySupport = signal<boolean>(false);
    $isCompactView = signal(false);
    $showMenu = signal(false);
    $extensionActive = signal(false);
    $featuresActive = signal(false);

    private tmpThemeColor = '';
    private currentTimeOfDay: TimeOfDayType;

    @HostBinding('class.dark')
    get isDark() {
        return this.$dark();
    }

    @HostBinding('class.transparent')
    get isTransparent() {
        return this.$transparent();
    }

    features: FeaturesTuple = [
        ['HEADER_V2.BASIC_FEATURES_GROUP', [
            { title: 'HEADER_V2.ACCOUNTING_TITLE', caption: 'HEADER_V2.ACCOUNTING_DESC', icon: iconFoldersArchive, link: RoutesEnum.ACCOUNTING },
            { title: 'HEADER_V2.SALES_TITLE', caption: 'HEADER_V2.SALES_DESC', icon: iconMoney, link: RoutesEnum.SALES },
            { title: 'HEADER_V2.PRODUCTS_TITLE', caption: 'HEADER_V2.PRODUCTS_DESC', icon: iconDelivery, link: RoutesEnum.PRODUCTS },
            { title: 'HEADER_V2.PARTNERS_TITLE', caption: 'HEADER_V2.PARTNERS_DESC', icon: iconHeart, link: RoutesEnum.PARTNERS },
            { title: 'HEADER_V2.TEAM_TITLE', caption: 'HEADER_V2.TEAM_DESC', icon: iconUsersGroup, link: RoutesEnum.TEAM }
        ]],
        ['HEADER_V2.EXT_FUNCTIONS_GROUP', [
            { title: 'HEADER_V2.INVENTORY_TITLE', caption: 'HEADER_V2.INVENTORY_DESC', icon: iconWarehouse, link: RoutesEnum.INVENTORY_MANAGEMENT },
            { title: 'HEADER_V2.TIME_TRACKING_TITLE', caption: 'HEADER_V2.TIME_TRACKING_DESC', icon: iconStopwatch, link: RoutesEnum.TIME_TRACKING }
        ]]
    ];

    extensions: FeaturesTuple = [
        ['', [
            { title: 'HEADER_V2.INVENTORY_TITLE', caption: 'HEADER_V2.INVENTORY_DESC_SHORT', icon: iconInventorySquare, link: RoutesEnum.INVENTORY_MANAGEMENT },
            { title: 'HEADER_V2.SERVICES_TITLE', caption: 'HEADER_V2.SERVICES_DESC', icon: iconServicesSquare, badge: { label: 'HEADER_V2.BADGE_DEV', style: 'primary' }, link: RoutesEnum.SERVICES },
            { title: 'HEADER_V2.RETAIL_TITLE', caption: 'HEADER_V2.RETAIL_DESC', icon: iconMobileSquare, badge: { label: 'HEADER_V2.BADGE_2028', style: 'framed-light' }, link: RoutesEnum.RETAIL },
            { title: 'HEADER_V2.ECOMMERCE_TITLE', caption: 'HEADER_V2.ECOMMERCE_DESC', icon: iconEcommerceSquare, badge: { label: 'HEADER_V2.BADGE_2029', style: 'framed-light' }, link: RoutesEnum.ECOMMERCE }
        ]]
    ];

    constructor(
        router: Router,
        media: MediaQuery,
        win: WindowService,
        private app: AppService,
        public localeService: LocaleService,
        private _modal: ModalService,
        private timeOfDayService: TimeOfDayService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        this.currentTimeOfDay = this.timeOfDayService.currentTimeOfDay;
        media.resized.pipe(takeUntilDestroyed()).subscribe(() => {
            this.$isCompactView.set(win.innerWidth <= 1050);
            if(!this.$isCompactView()) {
                this.$showMenu.set(false);
            }
        });
        this.$isCompactView.set(win.innerWidth <= 1050);
        router.events.pipe(takeUntilDestroyed()).subscribe((e) => {
            if(e instanceof NavigationEnd) {
                this.updateActiveMenu(e.url);
                this.$showMenu.set(false);
            }
            if(e instanceof NavigationSkipped) {
                this.$showMenu.set(false);
            }
        });
        this.updateActiveMenu(router.url);
        RouterBehavior.getRouteDataChanges().subscribe(data => {
            this.$dark.set(!!data.headerDark);
            this.$transparent.set(!!data.headerTransparent);
            this.$showOnlyLogin.set(!!data.headerLoginOnly);
            this.$showOnlyRegister.set(!!data.headerRegisterOnly);
            this.$showOnlySupport.set(!!data.headerSupportOnly);
            this.updateTransparentDark();
        });
        this.timeOfDayService.changed$
            .pipe(takeUntilDestroyed())
            .subscribe((timeOfDay) => {
                this.currentTimeOfDay = timeOfDay;
                this.updateTransparentDark();
            });
        effect(() => {
            if (!isPlatformBrowser(this.platformId)) {
                return;
            }
            if(this.$showMenu() && this.$isCompactView()) {
                this.document.body.classList.add('mobile-menu');
            } else {
                this.document.body.classList.remove('mobile-menu');
            }
            // if(this.$showMenu() && this.hasPromoLine && this.$isCompactView()) {
            //     if((win.scrollY || 0) < 92) {
            //         win.scrollTo(0, 92, 'smooth');
            //     }
            // }
        });
    }

    toggleMenu() {
        this.$showMenu.set(!this.$showMenu());
        if(this.$showMenu()) {
            this.tmpThemeColor =  this.app.themeColor;
            this.app.setThemeColor('#000');
        } else if(this.tmpThemeColor) {
            this.app.setThemeColor( this.tmpThemeColor);
        }
    }

    private updateActiveMenu(routerUrl: string) {
        this.$extensionActive.set(
            this.extensions.some(f =>
                f.some(i =>
                Array.isArray(i) &&
                i.some((l, index) => this.localeService.getLink(l.link) === decodeURIComponent(routerUrl) && index !== 0)
                )
            )
        );
        this.$featuresActive.set(
            this.features.some(f =>
                f.some(i =>
                Array.isArray(i) &&
                i.some(l => this.localeService.getLink(l.link) === decodeURIComponent(routerUrl))
                )
            )
        );
    }

    private updateTransparentDark() {
        if(this.$transparent()) {
            this.$dark.set(this.currentTimeOfDay === 'night' || this.currentTimeOfDay === 'evening');
        }
    }

    openSupport() {
         this._modal.open(
            import('../../../modals-v2/contact-support-modal/contact-support-modal.component').then(c => c.ContactSupportModalComponent),
            {
                hostClass: 'medium mobile-fs',
            },
        );
    }
}
